import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPolicyApplicationModel, selectCachedFormConfig, AppState } from '@client/model';
import {
  postDocusignEnvelopeForClientByFormIdRequest,
  postPolicyApplicationStepStatusUpdateRequest,
  updateSigningStatus,
} from '@client/model/policyApplicationModel';
import { Alert } from '@mui/material';
// Forms are now fetched dynamically from API via Redux cache
import { FullScreenLoadingOverlay } from '@client/components';

export function DocusignEmbeddedSigningPage() {
  const { clientPolicyApplicationId, policyApplicationFormId } = useParams<{
    clientPolicyApplicationId: string;
    policyApplicationFormId: string;
  }>();

  // Get form config from Redux cache (forms are fetched via API)
  const policyFormDef = useSelector((state: AppState) => 
    policyApplicationFormId ? selectCachedFormConfig(state, policyApplicationFormId) : undefined
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [docusignInitialized, setDocusignInitialized] = useState(false);
  const { embeddedSigningData, status, error } = useSelector(selectPolicyApplicationModel);

  const redirectUrl = searchParams.get('redirectUrl');
  const currentSignerRole = searchParams.get('currentSignerRole') as DocusignRoleKey;
  const recipientsParam = searchParams.get('recipients');
  const stepType = searchParams.get('stepType') as PolicyApplicationStepType;
  const forClientId = searchParams.get('forClientId');

  let recipients: DocusignRecipient[] | undefined;
  try {
    recipients = recipientsParam ? JSON.parse(recipientsParam) : undefined;
  } catch (error) {
    console.error('Failed to parse recipients from URL:', error);
    recipients = undefined;
  }

  useEffect(() => {
    if (
      clientPolicyApplicationId &&
      policyApplicationFormId &&
      policyFormDef &&
      !embeddedSigningData &&
      status !== 'creatingEmbeddedView' &&
      forClientId &&
      redirectUrl
    ) {
      dispatch(
        postDocusignEnvelopeForClientByFormIdRequest({
          clientPolicyApplicationId,
          formId: policyApplicationFormId,
          templateId: policyFormDef.formMetadata.templateId,
          mode: 'embedded-signing',
          returnUrl: redirectUrl,
          emailSubject: `Please review your policy application form`,
          recipients,
          currentSignerRole,
          forClientId,
        }),
      );
    }
  }, [
    clientPolicyApplicationId,
    policyApplicationFormId,
    policyFormDef,
    embeddedSigningData,
    redirectUrl,
    status,
    recipients,
    currentSignerRole,
    dispatch,
  ]);

  // Initialize DocuSign iframe when signing URL is available
  useEffect(() => {
    if (!embeddedSigningData?.signingUrl || !containerRef.current || docusignInitialized) {
      return;
    }

    const initializeDocuSign = () => {
      if (!containerRef.current) return;

      // Create iframe for DocuSign signing
      const iframe = document.createElement('iframe');
      iframe.src = embeddedSigningData.signingUrl;
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.title = 'DocuSign Signing';

      // Listen for completion messages from completion page
      const messageHandler = (event: MessageEvent) => {
        if (
          event.data &&
          typeof event.data === 'object' &&
          event.data.type === 'docusign_completion'
        ) {
          if (
            event.data.event === 'signing_complete' &&
            clientPolicyApplicationId &&
            currentSignerRole &&
            stepType
          ) {
            dispatch(updateSigningStatus('signingComplete'));
            dispatch(
              postPolicyApplicationStepStatusUpdateRequest({
                clientPolicyApplicationId,
                stepType,
                status: 'in_progress',
                currentSignerRole,
                docusignRecipientStatus: 'recipient_completed',
              }),
            );
          } else {
            navigate(-1);
          }
          window.removeEventListener('message', messageHandler);
        }
      };

      window.addEventListener('message', messageHandler);

      iframe.onload = () => {
        setDocusignInitialized(true);
        dispatch(updateSigningStatus('signing'));
      };

      iframe.onerror = () => {
        dispatch(updateSigningStatus('embeddedSigningError'));
      };

      // Mount iframe
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(iframe);

      // Cleanup
      return () => {
        window.removeEventListener('message', messageHandler);
      };
    };

    const timer = setTimeout(initializeDocuSign, 100);
    return () => clearTimeout(timer);
  }, [
    embeddedSigningData?.signingUrl,
    docusignInitialized,
    dispatch,
    navigate,
    clientPolicyApplicationId,
    stepType,
    currentSignerRole,
  ]);

  // Handle navigation after signing completion
  useEffect(() => {
    if (status === 'signingComplete' && redirectUrl) {
      const timer = setTimeout(() => {
        try {
          const url = new URL(redirectUrl);
          const targetPath = url.pathname + url.search;
          navigate(targetPath, { replace: true });
        } catch (error) {
          window.location.href = redirectUrl;
        }
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [status, redirectUrl, navigate]);

  if (
    status === 'creatingEmbeddedView' ||
    !redirectUrl ||
    (!embeddedSigningData?.signingUrl && status !== 'embeddedSigningError')
  ) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  // Show error states
  if (status === 'embeddedSigningError' || error || !policyFormDef) {
    return (
      <div className="p-8">
        <Alert severity="error">
          {error || 'Failed to initialize DocuSign. Please try again or contact support.'}
        </Alert>
        <div className="mt-4">
          <button
            onClick={() => window.location.reload()}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full">
      <FullScreenLoadingOverlay isLoading={status !== 'signing'} />

      <div
        ref={containerRef}
        className="h-full w-full"
        style={{ minHeight: '600px' }}
      />
    </div>
  );
}
